import get from 'platform/utilities/data/get';
import set from 'platform/utilities/data/set';
import { isClaimingIncrease, isClaimingNew } from './index';
import {
  makeSchemaForNewDisabilities,
  makeSchemaForRatedDisabilities,
} from './schemas';
import { reconcileDisabilityMap } from './reconcile-disability-map';
import { reconcilePowDisabilities } from './reconcile-pow-disabilities';

/**
 * Gets an array of all currently claimed disability IDs (both new and rated)
 * @param {Object} formData - The form data
 * @returns {string[]} Array of disability IDs (in sippableId format)
 */
export const getCurrentDisabilityIdArray = formData => {
  const ids = new Set();
  // Inline logic for new disabilities
  if (isClaimingNew(formData)) {
    const schema = makeSchemaForNewDisabilities(formData);
    Object.keys(schema.properties || {}).forEach(name => ids.add(name));
  }
  // Inline logic for rated disabilities
  if (isClaimingIncrease(formData)) {
    const ratedSchema = makeSchemaForRatedDisabilities(formData);
    Object.keys(ratedSchema.properties || {}).forEach(name => ids.add(name));
  }
  return Array.from(ids);
};

/**
 * Backward-compatible: Gets a Set of all currently claimed disability IDs (both new and rated)
 * @param {Object} formData - The form data
 * @returns {Set} Set of disability IDs (in sippableId format)
 */
export const getCurrentDisabilityNames = formData =>
  new Set(getCurrentDisabilityIdArray(formData));

/**
 * Helper to reconcile treatedDisabilityNames for a single facility (VA or non-VA).
 * Removes orphaned disabilities and optionally migrates renames.
 */
const reconcileFacility = ({
  facility,
  newFormData,
  oldFormData,
  canMigrateRenames,
}) => {
  if (!facility.treatedDisabilityNames) {
    return set('treatedDisabilityNames', {}, facility);
  }

  if (canMigrateRenames && oldFormData) {
    // Get old and new disabilities from the full form data
    const oldDisabilityIds = getCurrentDisabilityIdArray(oldFormData);
    const newDisabilityIds = getCurrentDisabilityIdArray(newFormData);

    // Build a list of renames (matching by position)
    const renames = [];
    if (oldDisabilityIds.length === newDisabilityIds.length) {
      for (let i = 0; i < newDisabilityIds.length; i += 1) {
        const oldId = oldDisabilityIds[i];
        const newId = newDisabilityIds[i];
        if (
          oldId !== newId &&
          facility.treatedDisabilityNames[oldId] !== undefined
        ) {
          renames.push({
            oldId,
            newId,
            value: facility.treatedDisabilityNames[oldId],
          });
        }
      }
    }

    // Apply renames and remove orphaned keys
    const next = { ...facility.treatedDisabilityNames };
    renames.forEach(({ oldId }) => {
      delete next[oldId];
    });
    renames.forEach(({ newId, value }) => {
      next[newId] = value;
    });

    // Remove keys that are no longer valid
    const validIds = new Set(newDisabilityIds);
    Object.keys(next).forEach(key => {
      if (!validIds.has(key)) {
        delete next[key];
      }
    });

    return set('treatedDisabilityNames', next, facility);
  }

  // No explicit rename migration possible (no oldFormData) - just remove orphaned keys
  const getDisabilitiesWrapper = _formData =>
    getCurrentDisabilityIdArray(newFormData).map(id => ({ id }));

  return reconcileDisabilityMap({
    oldFormData: undefined,
    newFormData: facility,
    mapPath: 'treatedDisabilityNames',
    getDisabilities: getDisabilitiesWrapper,
    keyForDisability: d => d.id,
    preservedKeys: [],
    migrateRenames: false,
  });
};

/**
 * Reconciles treatedDisabilityNames for a facility path by removing entries
 * for disabilities that no longer exist, and migrating values for renames.
 *
 * @param {string} pathKey - The path to the facilities array (e.g., 'vaTreatmentFacilities')
 * @param {Object} oldFormData - Previous form data (for rename detection)
 * @param {Object} newFormData - The form data to update
 * @returns {Object} Updated form data with synced disability names at the given path
 */
const syncFacilityPath = (pathKey, oldFormData, newFormData) => {
  const facilities = get(pathKey, newFormData);
  if (!facilities || !Array.isArray(facilities)) return newFormData;

  const oldFacilities = oldFormData ? get(pathKey, oldFormData) : null;

  // Only migrate renames if array lengths match (prevents mismatched positional matching)
  const canMigrateRenames =
    oldFormData &&
    oldFacilities &&
    Array.isArray(oldFacilities) &&
    oldFacilities.length === facilities.length;

  const reconciledFacilities = facilities.map(facility =>
    reconcileFacility({
      facility,
      newFormData,
      oldFormData,
      canMigrateRenames,
    }),
  );

  return set(pathKey, reconciledFacilities, newFormData);
};

/**
 * Reconciles treatedDisabilityNames in vaTreatmentFacilities and providerFacility
 * to remove entries for disabilities that no longer exist, and migrates values for renames.
 * Removes orphaned entries from the hashes entirely.
 *
 * @param {Object|undefined} oldFormData - Previous form data (for rename detection, optional)
 * @param {Object} newFormData - The form data to update
 * @returns {Object} Updated form data with synced disability names
 */
export const reconcileSupportingEvidence = (
  oldFormData = undefined,
  newFormData,
) => {
  let result = syncFacilityPath(
    'vaTreatmentFacilities',
    oldFormData,
    newFormData,
  );
  result = syncFacilityPath('providerFacility', oldFormData, result);
  return result;
};

/**
 * Helper to migrate a single orphaned key to a new ID in a disability map.
 * Returns the updated map if migration occurred, or the original map if not.
 *
 * @param {Object} disabilityMap - The map of disability IDs to values
 * @param {Set} validIds - Set of current valid disability IDs
 * @param {string} newId - The new disability ID to migrate to
 * @returns {Object|null} Updated map if migration occurred, null otherwise
 */
const migrateSingleOrphanedKey = (disabilityMap, validIds, newId) => {
  if (!disabilityMap || typeof disabilityMap !== 'object') return null;

  const orphanedKeys = Object.keys(disabilityMap).filter(
    key => !validIds.has(key),
  );

  // Only migrate if there's exactly one orphaned key
  if (
    orphanedKeys.length === 1 &&
    disabilityMap[orphanedKeys[0]] !== undefined
  ) {
    const oldId = orphanedKeys[0];
    const next = { ...disabilityMap };
    next[newId] = disabilityMap[oldId];
    delete next[oldId];
    return next;
  }

  return null;
};

/**
 * Reconciles single-condition renames in edit mode by detecting orphaned keys
 * and migrating them to the current disability ID at the given index.
 *
 * In edit mode, we know the array length hasn't changed and we're editing exactly
 * one condition. Any orphaned key in powDisabilities/treatedDisabilityNames is
 * assumed to be the old name of the edited condition, and we migrate it to the
 * new name.
 *
 * @param {Object} formData - The current form data
 * @param {number} editedIndex - The index of the condition being edited
 * @returns {Object} Updated form data with migrated keys
 */
export const reconcileSingleConditionRename = (formData, editedIndex) => {
  const currentDisabilityIds = getCurrentDisabilityIdArray(formData);
  const validIds = new Set(currentDisabilityIds);

  // Get the new ID at the edited index
  const newId = currentDisabilityIds[editedIndex];
  if (!newId) return formData;

  let result = formData;

  // Migrate powDisabilities
  const powDisabilities = formData?.['view:isPow']?.powDisabilities;
  const migratedPow = migrateSingleOrphanedKey(
    powDisabilities,
    validIds,
    newId,
  );
  if (migratedPow) {
    result = set('view:isPow.powDisabilities', migratedPow, result);
  }

  // Migrate treatedDisabilityNames in vaTreatmentFacilities
  const vaTreatmentFacilities = result?.vaTreatmentFacilities;
  if (Array.isArray(vaTreatmentFacilities)) {
    const updatedFacilities = vaTreatmentFacilities.map(facility => {
      const migratedNames = migrateSingleOrphanedKey(
        facility?.treatedDisabilityNames,
        validIds,
        newId,
      );
      return migratedNames
        ? set('treatedDisabilityNames', migratedNames, facility)
        : facility;
    });
    result = set('vaTreatmentFacilities', updatedFacilities, result);
  }

  // Migrate treatedDisabilityNames in providerFacility
  const providerFacility = result?.providerFacility;
  if (Array.isArray(providerFacility)) {
    const updatedProviders = providerFacility.map(facility => {
      const migratedNames = migrateSingleOrphanedKey(
        facility?.treatedDisabilityNames,
        validIds,
        newId,
      );
      return migratedNames
        ? set('treatedDisabilityNames', migratedNames, facility)
        : facility;
    });
    result = set('providerFacility', updatedProviders, result);
  }

  return result;
};

/**
 * Reconciles treatedDisabilityNames in vaTreatmentFacilities and providerFacility,
 * and powDisabilities in view:isPow, to remove entries for disabilities that
 * no longer exist, and migrates values for renames.
 * Removes orphaned entries from the hashes entirely.
 *
 * @param {Object|undefined} oldFormData - Previous form data (for rename detection, optional)
 * @param {Object} newFormData - The form data to update
 * @returns {Object} Updated form data with synced disability names
 */
export const reconcileSelectedDisabilities = (
  oldFormData = undefined,
  newFormData,
) => {
  // Handle both one-argument and two-argument calling conventions:
  // - reconcileSelectedDisabilities(formData) -> treat formData as newFormData
  // - reconcileSelectedDisabilities(oldData, newData) -> use both for rename detection
  const actualNewFormData =
    newFormData !== undefined ? newFormData : oldFormData;
  const actualOldFormData = newFormData !== undefined ? oldFormData : undefined;

  let result = reconcileSupportingEvidence(
    actualOldFormData,
    actualNewFormData,
  );
  result = reconcilePowDisabilities(actualOldFormData, result);
  return result;
};
